import { getInitials } from '../../lib/initials';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getConversations,
  getGroupConversations,
  deleteDirectConversation,
  leaveGroupConversation,
  type Conversation,
  type GroupConversation,
} from '../../services/api/messages';
import { NewConversationModal } from './NewConversationModal';

type UnifiedConversation =
  | { kind: 'direct'; data: Conversation }
  | { kind: 'group'; data: GroupConversation };

const timeAgo = (date: string) => {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'Nu';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} t`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} d`;
  return `${Math.floor(days / 30)} mdr`;
};

function TrashButton({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  return (
    <button
      onClick={onClick}
      title="Slet samtale"
      className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    </button>
  );
}

function DirectItem({ c, onDelete }: { c: Conversation; onDelete: () => void }) {
  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    if (window.confirm(`Slet samtalen med ${c.otherUser.name}? Dette fjerner alle beskeder for begge parter.`)) {
      onDelete();
    }
  };

  return (
    <div className={`group flex items-center border-b hover:bg-gray-50 transition ${c.unreadCount > 0 ? 'bg-blue-50' : 'bg-white'}`}>
      <Link
        to={`/messages/${c.otherUser.id}`}
        className="flex items-center gap-3 p-4 flex-1 min-w-0"
      >
        <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
          {c.otherUser.profileImageUrl ? (
            <img src={c.otherUser.profileImageUrl} alt={c.otherUser.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-xl text-gray-500">{getInitials(c.otherUser.name)}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-baseline mb-0.5">
            <p className="font-semibold text-gray-900 truncate">{c.otherUser.name}</p>
            <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{timeAgo(c.lastMessage.createdAt)}</span>
          </div>
          <p className={`text-sm truncate ${c.unreadCount > 0 ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
            {c.lastMessage.isSentByMe && 'Du: '}{c.lastMessage.content}
          </p>
        </div>
        {c.unreadCount > 0 && (
          <div className="bg-blue-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
            {c.unreadCount > 9 ? '9+' : c.unreadCount}
          </div>
        )}
      </Link>
      <div className="pr-3">
        <TrashButton onClick={handleDelete} />
      </div>
    </div>
  );
}

function GroupItem({ c, onDelete }: { c: GroupConversation; onDelete: () => void }) {
  const shown = c.participants.slice(0, 2);

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    if (window.confirm(`Forlad samtalen "${c.name}"?`)) {
      onDelete();
    }
  };

  return (
    <div className={`group flex items-center border-b hover:bg-gray-50 transition ${c.unreadCount > 0 ? 'bg-blue-50' : 'bg-white'}`}>
      <Link
        to={`/messages/group/${c.conversationId}`}
        className="flex items-center gap-3 p-4 flex-1 min-w-0"
      >
        <div className="w-12 h-12 relative flex-shrink-0">
          {shown.map((p, i) => (
            <div key={p.id} className={`absolute w-8 h-8 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center overflow-hidden ${i === 0 ? 'top-0 left-0' : 'bottom-0 right-0'}`}>
              {p.profileImageUrl ? (
                <img src={p.profileImageUrl} alt={p.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs font-semibold text-gray-500">{getInitials(p.name)}</span>
              )}
            </div>
          ))}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-baseline mb-0.5">
            <p className="font-semibold text-gray-900 truncate flex items-center gap-1">
              <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {c.name}
            </p>
            {c.lastMessage && <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{timeAgo(c.lastMessage.createdAt)}</span>}
          </div>
          {c.lastMessage ? (
            <p className={`text-sm truncate ${c.unreadCount > 0 ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
              {c.lastMessage.isSentByMe ? 'Du: ' : `${c.lastMessage.senderName}: `}{c.lastMessage.content}
            </p>
          ) : (
            <p className="text-sm text-gray-400 italic">Ingen beskeder endnu</p>
          )}
        </div>
        {c.unreadCount > 0 && (
          <div className="bg-blue-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
            {c.unreadCount > 9 ? '9+' : c.unreadCount}
          </div>
        )}
      </Link>
      <div className="pr-3">
        <TrashButton onClick={handleDelete} />
      </div>
    </div>
  );
}

export const ConversationList = () => {
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userId: activeUserId, conversationId: activeConvId } = useParams<{ userId?: string; conversationId?: string }>();

  const { data: directConvs = [], isLoading: loadingDirect } = useQuery({
    queryKey: ['conversations'],
    queryFn: getConversations,
    refetchInterval: 10000,
  });

  const { data: groupConvs = [], isLoading: loadingGroups } = useQuery({
    queryKey: ['group-conversations'],
    queryFn: getGroupConversations,
    refetchInterval: 10000,
  });

  const deleteDirectMutation = useMutation({
    mutationFn: deleteDirectConversation,
    onSuccess: (_data, otherUserId) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
      if (activeUserId === otherUserId) navigate('/messages');
    },
  });

  const leaveGroupMutation = useMutation({
    mutationFn: leaveGroupConversation,
    onSuccess: (_data, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['group-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
      if (activeConvId === conversationId) navigate('/messages');
    },
  });

  const unified: UnifiedConversation[] = [
    ...directConvs.map((c): UnifiedConversation => ({ kind: 'direct', data: c })),
    ...groupConvs.map((c): UnifiedConversation => ({ kind: 'group', data: c })),
  ].sort((a, b) => {
    const aTime = a.kind === 'direct'
      ? new Date(a.data.lastMessage.createdAt).getTime()
      : a.data.lastMessage ? new Date(a.data.lastMessage.createdAt).getTime() : 0;
    const bTime = b.kind === 'direct'
      ? new Date(b.data.lastMessage.createdAt).getTime()
      : b.data.lastMessage ? new Date(b.data.lastMessage.createdAt).getTime() : 0;
    return bTime - aTime;
  });

  const isLoading = loadingDirect && loadingGroups;

  return (
    <>
      <div className="p-4 border-b bg-white sticky top-0 z-10 flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Beskeder</h2>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1 px-3 py-1.5 bg-primary-green text-white text-sm font-medium rounded-lg hover:bg-secondary-green transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ny besked
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-green" />
        </div>
      ) : unified.length === 0 ? (
        <div className="text-center py-12 text-gray-500 px-4">
          <svg className="mx-auto w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <p className="font-medium mb-1">Ingen beskeder endnu</p>
          <p className="text-sm">Tryk på "Ny besked" for at starte en samtale</p>
        </div>
      ) : (
        <div className="divide-y">
          {unified.map((item) =>
            item.kind === 'direct' ? (
              <DirectItem
                key={item.data.conversationId}
                c={item.data}
                onDelete={() => deleteDirectMutation.mutate(item.data.otherUser.id)}
              />
            ) : (
              <GroupItem
                key={`group-${item.data.conversationId}`}
                c={item.data}
                onDelete={() => leaveGroupMutation.mutate(item.data.conversationId)}
              />
            )
          )}
        </div>
      )}

      {showModal && <NewConversationModal onClose={() => setShowModal(false)} />}
    </>
  );
};
